import { inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { NotificationsCounterSelectors } from '@core/activities-and-notifications-data';
import { filterBoolean } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';

@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly i18n = inject(I18nService);
  private readonly titleService = inject(Title);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private defaultTitle: string = '- Recruitee';
  private currentTitleKey: string;
  private currentTitle: string;
  private currentUnreadedCount: number = 0;
  private defaultTitleAlreadyAdded: boolean = false;

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.defaultTitleAlreadyAdded = false;
        let title = this.getTitle(this.activatedRoute.root)
          .map(titleKey => {
            this.currentTitleKey = titleKey;
            return titleKey !== 'custom'
              ? this.i18n.translate(`app.page_titles.${titleKey}`)
              : undefined;
          })
          .join(' - ');

        // hack for none ng2-router endpoints
        if (!title || typeof title === 'undefined') {
          const titleKey = this.getKeyFromUrl();
          this.currentTitleKey = titleKey;
          const missingString = 'missing';
          title = this.i18n.translate(`app.page_titles.${titleKey}`);
          if (title.toLowerCase().indexOf(missingString.toLowerCase()) !== -1) {
            title = this.i18n.translate(`app.page_titles.default`);
          }
        }
        if (this.currentTitleKey !== 'custom') {
          this.setCustomTitle(title);
        }
      }
    });

    this.store
      .select(NotificationsCounterSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(data => {
        this.currentUnreadedCount = data.unreadCount;
        this.setCustomTitle(this.currentTitle);
      });
  }

  public setCustomTitle(title: string): void {
    this.currentTitle = this.defaultTitleAlreadyAdded ? title : `${title} ${this.defaultTitle}`;
    this.defaultTitleAlreadyAdded = true;
    if (this.currentUnreadedCount) {
      this.titleService.setTitle(`(${this.currentUnreadedCount}) ${this.currentTitle}`);
    } else {
      this.titleService.setTitle(`${this.currentTitle}`);
    }
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  private getKeyFromUrl(): string {
    const url = window.location.href;
    const array = url.split('/');
    const lastsegment = array[array.length - 1];

    return lastsegment.split('?')[0];
  }

  private getTitle(root: ActivatedRoute): string[] {
    const parts: string[] = [];
    let route = root;
    while (route) {
      const { title } = route.snapshot.data || {};
      if (title && !parts.includes(title)) {
        parts.push(title);
      }
      route = route.children[0];
    }

    return parts;
  }
}
